<template>
  <div
    v-if="hasCalculator"
    class="package-calculator mt-2">
    <div v-if="selectedVariantName && !(hasCalculatorWithCustomChoice && canEditCalculatedService)">
      <small>
        {{ $t('service.servicesPackageVariant') }}:
        <span class="text-primary">{{ selectedVariantName }}</span>
      </small>

      <small
        v-if="manualOverride"
        class="d-block text-warning">
        {{ $t('service.servicesPackageManuallyModified') }}
      </small>
    </div>

    <d-alert
      v-if="showConfigureAlert && hasCalculatorWithLevels"
      :show="true"
      theme="warning"
      class="px-2 py-1 mt-1 mb-1">
      <small>{{ $t('service.servicesPackage.configureService') }}</small>
    </d-alert>

    <d-alert
      v-else-if="showConfigureAlert && !hasCalculatorWithCustomChoice"
      :show="true"
      theme="warning"
      class="px-2 py-1 mt-1 mb-1">
      <small>{{ $t('service.servicesPackage.configureServiceWithoutLevels') }}</small>
    </d-alert>

    <d-alert
      :show="hasWrongDimensions"
      theme="danger"
      class="px-2 py-1 mt-1 mb-1">
      <small>{{ $t('service.servicesPackageInvalidConfig.wrongDimensions') }}</small>
    </d-alert>

    <d-alert
      :show="hasNoMatchingService"
      theme="danger"
      class="px-2 py-1 mt-1 mb-1">
      <small>{{ $t('service.servicesPackageInvalidConfig.noMatchingService') }}</small>
    </d-alert>

    <d-button
      v-if="!canEditCalculatedService"
      size="sm"
      @click.prevent="allowEditOfCalculatedService"
      class="w-100 mb-2 mt-2">
      {{ $t('service.servicesPackageModify') }}
    </d-button>

    <div
      v-if="hasCalculatorWithCustomChoice && canEditCalculatedService"
      class="mt-2">
      <validation-provider
        rules="required"
        :name="'service-selection-for-package-' + packageData.id"
        v-slot="{ errors }"
        tag="div">
        <div :class="{
          'form-control': true,
          'is-select': true,
          'is-invalid': errors.length
        }">
          <v-select
            class="w-100"
            :options="calculatorServices"
            v-model="customServiceSelection"
            :reduce="service => service.id"
            :placeholder="$t('service.chooseService')"
            label="code"
            :filterBy="filterByForServices">
            <template slot="no-options">
              {{ $t('service.noServicesMatchingPhrase') }}&hellip;
            </template>
            <template slot="option" slot-scope="option">
              <div class="d-center w-100 normal-white-space">
                {{ option.name }}
              </div>
            </template>
            <template slot="selected-option" slot-scope="option">
              <div class="selected d-center w-100 normal-white-space">
                {{ option.name }}
              </div>
            </template>
          </v-select>
        </div>
      </validation-provider>
    </div>

    <template v-if="!hasCalculatorWithCustomChoice && canEditCalculatedService">
      <validation-provider
        rules="required"
        :name="'service-calculated-for-package-' + packageData.id"
        v-slot="{ errors }"
        tag="div">
        <input
          type="hidden"
          v-model="calculatedServiceID">
        <small
          v-if="errors.length"
          class="text-danger d-block">
          {{ $t('service.servicesPackage.configureServiceValidation') }}
        </small>
      </validation-provider>

      <table
        v-if="hasCalculatorWithLevels"
        class="w-100 no-border-bottom mt-2">
        <tr>
          <td
            style="width: 180px;"
            class="align-middle">
            <small>{{ $t('service.complexity') }}</small>
          </td>
          <td>
            <div class="d-flex image-tooltips">
              <d-button-group size="small">
                <d-button
                  :id="'complexity-easy-' + tooltipsScopeID"
                  theme="primary"
                  :outline="complexityLevel !== 'EASY'"
                  @click.prevent="complexityLevel = 'EASY'">
                  EASY
                </d-button>
                <d-button
                  :id="'complexity-medium-' + tooltipsScopeID"
                  theme="primary"
                  :outline="complexityLevel !== 'MEDIUM'"
                  @click.prevent="complexityLevel = 'MEDIUM'">
                  MEDIUM
                </d-button>
                <d-button
                  :id="'complexity-hard-' + tooltipsScopeID"
                  theme="primary"
                  :outline="complexityLevel !== 'HARD'"
                  @click.prevent="complexityLevel = 'HARD'">
                  HARD
                </d-button>

                <d-tooltip
                  :target="'#complexity-easy-' + tooltipsScopeID"
                  container=".image-tooltips"
                  :triggers="['hover']">
                  <img
                    src="@/assets/images/complexity-levels/easy.png"
                    alt="" />
                </d-tooltip>

                <d-tooltip
                  :target="'#complexity-medium-' + tooltipsScopeID"
                  container=".image-tooltips"
                  :triggers="['hover']">
                  <img
                    src="@/assets/images/complexity-levels/medium.png"
                    alt="" />
                </d-tooltip>

                <d-tooltip
                  :target="'#complexity-hard-' + tooltipsScopeID"
                  container=".image-tooltips"
                  :triggers="['hover']">
                  <img
                    src="@/assets/images/complexity-levels/hard.png"
                    alt="" />
                </d-tooltip>
              </d-button-group>
            </div>
          </td>
        </tr>
      </table>

      <table class="w-100 no-border-bottom mt-1">
        <tr>
          <td
            style="width: 180px;"
            class="align-middle">
            <small>
              <template v-if="calculatorUnit === 'mm'">
                {{ $t('service.embroidery.sizes.mm') }}
              </template>
              <template v-else>
                {{ $t('service.embroidery.sizes.cm') }}
              </template>
            </small>
          </td>
          <td>
            <div class="d-flex" style="max-width: 360px;">
              <d-input-group class="mr-1">
                <d-input
                  size="sm"
                  type="number"
                  v-model="embroideryWidth"
                  class="text-right" />
                <d-input-group-text slot="append">
                  {{ $t('service.embroidery.width') }}
                </d-input-group-text>
              </d-input-group>
              <d-input-group>
                <d-input
                  size="sm"
                  type="number"
                  v-model="embroideryHeight"
                  class="text-right" />
                <d-input-group-text slot="append">
                  {{ $t('service.embroidery.height') }}
                </d-input-group-text>
              </d-input-group>
            </div>
          </td>
        </tr>
      </table>
    </template>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'order-creator-service-package-calculator',
  props: [
    'packageData',
    'group',
    'calculatedService',
    'productIndex',
    'readOnly',
    'orderSetUpInProgress'
  ],
  computed: {
    hasCalculator () {
      return !!(this.group && this.group.has_stitch_calculator);
    },
    hasCalculatorWithLevels () {
      return this.hasCalculator && this.group.calculator_type === 'embroidery';
    },
    hasCalculatorWithCustomChoice () {
      return this.hasCalculator && this.group.calculator_type === 'custom';
    },
    calculatorUnit () {
      return this.hasCalculatorWithLevels ? 'mm' : 'cm';
    },
    calculatorServices () {
      if (this.group && this.group.calculator_services) {
        return this.group.calculator_services;
      }

      return [];
    },
    isNewPackage () {
      return !!(this.packageData && this.packageData.id && (this.packageData.id).toString().indexOf('new-package-') === 0);
    },
    canEditCalculatedService () {
      return this.userAllowedEditCalculatedService || this.isNewPackage;
    },
    tooltipsScopeID () {
      return (this.packageData.id).toString().replace(/[^a-z0-9]/gi, '-');
    },
    selectedVariantName () {
      if (this.calculatedService && this.calculatedService.id && this.calculatedService.name) {
        return this.calculatedService.name;
      }

      return null;
    },
    hasServiceSet () {
      return !!(this.calculatedService && this.calculatedService.id);
    },
    showConfigureAlert () {
      return !this.hasServiceSet && this.canEditCalculatedService && !this.hasNoMatchingService && !this.hasWrongDimensions;
    },
    calculatedServiceID: {
      get () {
        return this.calculatedService && this.calculatedService.id ? this.calculatedService.id : '';
      },
      set () {
        // read-only proxy used for validation purposes only
      }
    },
    embroideryWidth: {
      get () {
        return this.calculatedService ? this.calculatedService.embroidery_width : null;
      },
      set (newValue) {
        if (this.calculatedService) {
          this.$set(this.calculatedService, 'embroidery_width', newValue);
        }
      }
    },
    embroideryHeight: {
      get () {
        return this.calculatedService ? this.calculatedService.embroidery_height : null;
      },
      set (newValue) {
        if (this.calculatedService) {
          this.$set(this.calculatedService, 'embroidery_height', newValue);
        }
      }
    },
    groupEmbroideryType () {
      if (this.group && this.group.uses_embroidery && this.group.embroidery_type_id) {
        return {
          embroidery_type_id: this.group.embroidery_type_id,
          embroidery_type_name: this.group.embroidery_type_name
        };
      }

      return null;
    },
    calculatorTrigger () {
      if (!this.hasCalculator || this.hasCalculatorWithCustomChoice) {
        return false;
      }

      return this.complexityLevel + '|' + this.embroideryWidth + 'x' + this.embroideryHeight;
    }
  },
  data () {
    return {
      complexityLevel: null,
      customServiceSelection: null,
      hasWrongDimensions: false,
      hasNoMatchingService: false,
      lastServiceFindRequestIndex: 0,
      userAllowedEditCalculatedService: false,
      lastAppliedServiceID: null,
      manualOverride: false
    };
  },
  watch: {
    calculatorTrigger () {
      if (this.orderSetUpInProgress || this.readOnly) {
        return;
      }

      if (this.canEditCalculatedService) {
        this.debouncedFindServiceByParams();
      }
    },
    customServiceSelection (newValue) {
      if (this.orderSetUpInProgress || this.readOnly) {
        return;
      }

      let currentServiceID = this.calculatedService && this.calculatedService.id ? this.calculatedService.id : null;

      if (newValue === currentServiceID) {
        return;
      }

      if (!newValue) {
        this.emitCalculatorResult(null, null);
        return;
      }

      let usedService = this.calculatorServices.filter(service => service.id === newValue);

      if (usedService && usedService[0]) {
        this.emitCalculatorResult(usedService[0].id, usedService[0].name);
      }
    },
    'calculatedService.id': function (newValue) {
      if (this.hasCalculatorWithCustomChoice) {
        this.customServiceSelection = newValue || null;
      }

      if (!newValue) {
        this.manualOverride = false;
        return;
      }

      if (this.lastAppliedServiceID && newValue !== this.lastAppliedServiceID) {
        this.manualOverride = true;
        return;
      }

      this.manualOverride = false;
    }
  },
  created () {
    this.debouncedFindServiceByParams = debounce(() => {
      this.findServiceByParams();
    }, 1000);
  },
  mounted () {
    if (this.calculatedService && this.calculatedService.id) {
      this.lastAppliedServiceID = this.calculatedService.id;

      if (this.hasCalculatorWithCustomChoice) {
        this.customServiceSelection = this.calculatedService.id;
      }
    }
  },
  methods: {
    filterByForServices (option, label, search) {
      return (option.code + ' ' + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    allowEditOfCalculatedService () {
      this.userAllowedEditCalculatedService = true;
      this.complexityLevel = null;
      this.customServiceSelection = null;
      this.manualOverride = false;
      this.emitCalculatorResult(null, null);
    },
    emitCalculatorResult (serviceID, serviceName) {
      this.lastAppliedServiceID = serviceID;

      let payload = {
        packageID: this.packageData.id,
        productIndex: this.productIndex !== undefined ? this.productIndex : null,
        serviceID,
        serviceName,
        embroideryTypeID: null,
        embroideryTypeName: null
      };

      if (serviceID && this.groupEmbroideryType) {
        payload.embroideryTypeID = this.groupEmbroideryType.embroidery_type_id;
        payload.embroideryTypeName = this.groupEmbroideryType.embroidery_type_name;
      }

      this.$bus.$emit('order-package-calculator-result', payload);
    },
    findServiceByParams () {
      if (
        !this.hasCalculator ||
        this.hasCalculatorWithCustomChoice ||
        this.readOnly ||
        this.orderSetUpInProgress ||
        !this.canEditCalculatedService
      ) {
        return;
      }

      if (
        !this.embroideryWidth ||
        !this.embroideryHeight ||
        (this.hasCalculatorWithLevels && !this.complexityLevel)
      ) {
        return;
      }

      this.hasNoMatchingService = false;
      this.hasWrongDimensions = false;

      if (
        parseFloat(this.embroideryWidth) === 0 ||
        parseFloat(this.embroideryHeight) === 0 ||
        Number.isNaN(parseFloat(this.embroideryWidth)) ||
        Number.isNaN(parseFloat(this.embroideryHeight))
      ) {
        this.hasWrongDimensions = true;
        return;
      }

      let currentRequestIndex = ++this.lastServiceFindRequestIndex;
      let formDataToSend = {
        service_group_id: this.packageData.group_id,
        width: parseFloat(this.embroideryWidth),
        height: parseFloat(this.embroideryHeight)
      };

      if (this.hasCalculatorWithLevels) {
        formDataToSend.level = this.complexityLevel;
      }

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/services/find-by-calculator',
        formData: formDataToSend,
        skipErrorDisplay: true,
        successAction: (response) => {
          if (currentRequestIndex !== this.lastServiceFindRequestIndex) {
            return;
          }

          if (!response || !response.data || !response.data.id) {
            this.hasNoMatchingService = true;
            this.emitCalculatorResult(null, null);
            return;
          }

          this.emitCalculatorResult(response.data.id, response.data.name);
        },
        failAction: () => {
          if (currentRequestIndex !== this.lastServiceFindRequestIndex) {
            return;
          }

          this.hasNoMatchingService = true;
          this.emitCalculatorResult(null, null);
        }
      });
    }
  },
  beforeDestroy () {
    this.debouncedFindServiceByParams.cancel();
  }
}
</script>

<style lang="scss" scoped>
.package-calculator {
  font-weight: 400;
}

.normal-white-space {
  line-height: 1.2;
  white-space: normal;
}

.no-border-bottom {
  border-bottom: none;

  td {
    border-bottom: none;
  }
}
</style>
